import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { GetShowcases, GetShowcaseItems } from "../../../api/queries";
import { Showcase, ShowcaseItem } from "../../../api/zod-types";
import ProductNotFound from "../ProductNotFound";
import CTAExploreMore from "../CTAExploreMore";
import { SafeLink } from "@/components/ui/SafeLink";
import { useLocation } from "react-router-dom";
import { useEffect } from "react";



function slugify(str: string) {
    return str.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

export default function ShowcaseItemListShowcase() {
    

    const location = useLocation();
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [location.pathname]);

    const navigate = useNavigate();
    const queryClient = useQueryClient();

    function handlePrefetchProductDetail(productId: string) {
        queryClient.prefetchQuery({
            queryKey: ["showcaseitemdetail", productId],
            queryFn: () => GetShowcaseItems(),
        });
    }

    const { category, subCategory, showcase } = useParams<{ category?: string; subCategory?: string; showcase?: string }>();
    const [selectedTag, setSelectedTag] = React.useState<string | null>(null);

    // Fetch all showcases and showcase items
    const { data: showcasesData, isLoading: isLoadingShowcases, isError: isErrorShowcases } = useQuery({
        queryKey: ["showcases"],
        queryFn: GetShowcases,
        staleTime: 60 * 1000 * 5,
    });
    const { data: showcaseItemsData, isLoading: isLoadingItems, isError: isErrorItems } = useQuery({
        queryKey: ["showcaseitems"],
        queryFn: GetShowcaseItems,
        staleTime: 60 * 1000 * 5,
    });

    if (isLoadingShowcases || isLoadingItems) {
        return <div className="text-center py-12 text-lg text-gray-500">Loading...</div>;
    }

    if (isErrorShowcases || isErrorItems || !showcasesData || !showcaseItemsData) {
        return <div className="text-center py-12 text-lg text-red-500">Failed to load showcase items.</div>;
    }

    // Parse showcases and items
    let showcases: Showcase[] = [];
    if (showcasesData?.data) {
        if (Array.isArray(showcasesData.data)) {
            showcases = showcasesData.data;
        } else if (Array.isArray(showcasesData.data.results)) {
            showcases = showcasesData.data.results;
        } else {
            console.warn("showcasesData.data.results is not an array", showcasesData.data.results);
        }
    }
    let showcaseItems: ShowcaseItem[] = [];
    if (showcaseItemsData?.data) {
        if (Array.isArray(showcaseItemsData.data)) {
            showcaseItems = showcaseItemsData.data;
        } else if (Array.isArray(showcaseItemsData.data.results)) {
            showcaseItems = showcaseItemsData.data.results;
        } else {
            console.warn("showcaseItemsData.data.results is not an array", showcaseItemsData.data.results);
        }
    }

    // Filter showcases for the selected subCategory
    const filteredShowcases = Array.isArray(showcases) ? showcases.filter(
        s => s.showcasecategoryname && slugify(s.showcasecategoryname) === slugify(subCategory || "")
    ) : [];

    // Get all showcase items linked to the selected showcase
    const showcaseNames = filteredShowcases.map(s => s.showcasename);

    let filteredItems = Array.isArray(showcaseItems) ? showcaseItems.filter(
        item => showcaseNames.includes(item.showcasename) &&
            slugify(item.showcasename) === slugify(showcase || "")
    ) : [];



    // Tag counts for badge (from showcaseitemfiltertag)
    const tagCounts: Record<string, number> = {};
    filteredItems.forEach(item => {
        (item.showcaseitemfiltertag || "")
            .split(",")
            .map(t => t.trim())
            .filter(Boolean)
            .forEach(tag => {
                tagCounts[tag] = (tagCounts[tag] || 0) + 1;
            });
    });

    // Get unique tags from showcaseitemfiltertag (comma-separated)
    const tags = Array.from(
        new Set(
            filteredItems.flatMap(item =>
                item.showcaseitemfiltertag
                    ? item.showcaseitemfiltertag.split(",").map(t => t.trim())
                    : []
            )
        )
    ).filter(Boolean);

    // Apply tag filter if selected
    if (selectedTag) {
        filteredItems = filteredItems.filter(item =>
            item.showcaseitemfiltertag &&
            item.showcaseitemfiltertag.split(",").map(t => t.trim()).includes(selectedTag)
        );
    }

    const breadcrumb = (
        <nav className="text-sm mb-6" aria-label="Breadcrumb">
            <ol className="flex flex-wrap gap-2 text-gray-500">
            <li >
                <SafeLink className="hover:underline" to="/">Home</SafeLink>                
            </li>
            <li>/</li>
            <li >
                <SafeLink className="hover:underline" to="/products">Products</SafeLink>                
            </li>
            <li>/</li>
            {category && (
                <li >
                    <SafeLink className="hover:underline" to={`/products/${category}`}>
                        {category.replace(/-/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                    </SafeLink>
                </li>
            )}
            <li>/</li>
            {subCategory && (
                <li >
                    <SafeLink className="hover:underline" to={`/products/${category}/${subCategory}`}>
                        {subCategory.replace(/-/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                    </SafeLink>
                </li>
            )}
            <li>/</li>
            {showcase && (
                <li className=" text-black font-semibold">
                    {showcase.replace(/-/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                </li>
            )}
        </ol>
        </nav>
    );
    // Find the selected showcase object for hero section
    const selectedShowcaseObj = filteredShowcases.find(
        s => slugify(s.showcasename) === slugify(showcase || "")
    );

    // Hero section text
    const heroTitle = selectedShowcaseObj?.showcasename
        ? selectedShowcaseObj.showcasename.replace(/-/g, " ").replace(/\b\w/g, l => l.toUpperCase())
        : "Showcase";
    const heroDescription = selectedShowcaseObj?.showcasecategoryname
        ? `Explore our premium selection of ${heroTitle.toLowerCase()} in the ${selectedShowcaseObj.showcasecategoryname.toLowerCase()} category. Find the perfect product for your needs.`
        : `Explore our premium selection of ${heroTitle.toLowerCase()}. Find the perfect product for your needs.`;
    const heroImage = selectedShowcaseObj?.showcaseimageurl || "/fallback-image.jpg";

    // Only show ProductNotFound if not loading and filteredItems is truly empty
    if (!isLoadingShowcases && !isLoadingItems && filteredItems.length === 0) {
        return (
            <div className="flex flex-col w-full bg-gradient-to-b from-[#f8fafc] to-[#f2f2f2] pb-12 min-h-screen px-2 justify-center items-center">
                <div className="max-w-lg w-full mx-auto">
                    {breadcrumb}
                    <ProductNotFound onGoBack={() => navigate(`/products/${category}`)} />
                </div>
            </div>
        );
    }

    return (
        filteredItems.length === 0 ? (
            <div className="flex flex-col w-full bg-gradient-to-b from-[#f8fafc] to-[#f2f2f2] pb-12 min-h-screen px-2 justify-center items-center">
                <div className="max-w-lg w-full mx-auto">
                    {breadcrumb}
                    <ProductNotFound onGoBack={() => navigate(`/products/${category}`)} />
                </div>
            </div>
        ) : (
            <div className="flex flex-col w-full bg-gradient-to-b from-[#f8fafc] to-[#f2f2f2] pb-12 min-h-screen ">
                
                {/* Hero Section - full width, background image, overlay */}
                <section
                    className="relative w-full h-[340px] md:h-[420px] flex items-center justify-center mb-8"
                    style={
                        heroImage && heroImage !== "/fallback-image.jpg"
                            ? {
                                backgroundImage: `url(${heroImage})`,
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                            }
                            : {
                                backgroundColor: "#111",
                            }
                    }
    
                >
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-black/80 backdrop-blur-sm"></div>
                    {/* Decorative elements (optional) */}
                    <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-red-400 rounded-full animate-ping opacity-60"></div>
                    <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-white rounded-full animate-pulse opacity-80"></div>
                    {/* Main hero content */}
                    <div className="relative z-10 flex flex-col items-center justify-center w-full h-full text-center px-4">
                        <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-6 animate-fade-in-up">
                            <div className="w-2 h-2 bg-red-400 rounded-full animate-pulse"></div>
                            <span className="text-sm font-medium text-white/90 uppercase tracking-wider">
                                {selectedShowcaseObj?.showcasecategoryname || "Showcase"}
                            </span>
                        </div>
                        <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold py-4 text-white animate-fade-in-up delay-200">
                            {heroTitle}
                        </h1>
                        <p className="text-lg md:text-xl text-white/90 py-4 animate-fade-in-up delay-400 max-w-2xl mx-auto">
                            {heroDescription}
                        </p>
                        {/* Optional CTA */}
                        {/* <button className="bg-gradient-to-r from-red-600 to-red-700 text-white font-semibold px-8 py-3 rounded-full shadow-lg hover:scale-105 transition-all">
                            Enquire Now
                        </button> */}
                    </div>
                </section>

                <div className="w-full max-w-3xl lg:max-w-7xl xl:max-w-full mx-auto px-4 md:px-8 lg:px-16 xl:px-32 mt-10 mb-8">
                    {/* <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 text-center mb-2 tracking-tight capitalize">
                        {showcase?.replace(/-/g, " ").replace(/\b\w/g, (l: string) => l.toUpperCase())}
                    </h1>
                    <p className="text-lg md:text-xl text-gray-600 text-center mb-6">
                        Discover our range of {showcase?.replace(/-/g, " ").toLowerCase()} products.
                    </p> */}
                    {breadcrumb}
                </div>
                <div className="w-full max-w-3xl lg:max-w-7xl xl:max-w-full mx-auto px-4 md:px-8 lg:px-16 xl:px-32 mt-10 mb-8 flex flex-col md:flex-row gap-8">
                    <div className="w-full md:w-1/4 flex-shrink-0 py-2 lg:py-0">
                        <aside className="w-full bg-white rounded-xl shadow-lg border border-gray-100 p-6 h-fit mb-8 lg:mb-0 sticky self-start transition-shadow">
                            <h2 className="text-xl font-bold mb-4 text-black">Categories</h2>
                            <div className="flex flex-col gap-2 mb-6">
                                <button
                                    className={`px-3 py-1 rounded-full border text-sm font-medium flex items-center justify-center gap-2 transition focus:outline-none focus:ring-2 focus:ring-harvey-red ${!selectedTag
                                        ? "bg-harvey-red text-white border-harvey-red shadow"
                                        : "bg-white text-harvey-red border-harvey-red hover:bg-harvey-red hover:text-white"
                                        }`}
                                    onClick={() => setSelectedTag(null)}
                                >
                                    <span className="flex flex-row items-center justify-center">
                                        All
                                        <span className="ml-2 text-xs leading-none">({filteredItems.length})</span>
                                    </span>
                                </button>
                                {tags.map(tag => (
                                    <button
                                        key={tag}
                                        className={`px-3 py-1 rounded-full border text-sm font-medium flex items-center justify-center gap-2 transition focus:outline-none focus:ring-2 focus:ring-harvey-red ${selectedTag === tag
                                            ? "bg-harvey-red text-white border-harvey-red shadow"
                                            : "bg-white text-harvey-red border-harvey-red hover:bg-harvey-red hover:text-white"
                                            }`}
                                        onClick={() => setSelectedTag(tag)}
                                    >
                                        <span className="flex flex-row items-center justify-center">
                                            {tag}
                                            <span className="ml-2 text-xs leading-none">({tagCounts[tag]})</span>
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </aside>
                    </div>
                    <div className="hidden lg:block w-px bg-gray-200 mx-2 rounded-full" />
                    <main className="w-full md:w-3/4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 ">
                            {filteredItems.map((showcaseitem: ShowcaseItem, idx) => (
                                <div
                                    key={showcaseitem.productid || showcaseitem.showcaseitemname}
                                    className="bg-white rounded-xl shadow-lg p-6 flex flex-col items-center cursor-pointer hover:shadow-2xl transition group animate-fade-in"
                                    style={{ animationDelay: `${idx * 40}ms` }}
                                    tabIndex={0}
                                    onClick={() => navigate(
                                        `/products/${category}/${subCategory}/${showcase}/${slugify(showcaseitem.productname)}`,
                                        { state: { product: showcaseitem } }
                                    )}
                                    onMouseEnter={() => handlePrefetchProductDetail(showcaseitem.productid)}
                                    onKeyPress={e => {
                                        if (e.key === "Enter") {
                                            navigate(
                                                `/products/${category}/${subCategory}/${showcase}/${slugify(showcaseitem.productname)}`,
                                                { state: { product: showcaseitem } }
                                            );
                                        }
                                    }}
                                >
                                    <img
                                        // src={showcaseitem.productimage || "https://hncnzwww.blob.core.windows.net/www/img/products/hnc-furniture-bedding-education-hero.jpg"}
                                        src={showcaseitem.productimage}
                                        alt={showcaseitem.showcasename || ""}
                                        className="w-32 h-32  rounded-xl mb-4 group-hover:scale-105 transition-transform"
                                    />
                                    <h2 className="text-center text-black font-semibold">{showcaseitem.productname || "No Product Name"}</h2>
                                    <p className="text-center text-gray-500 text-sm">{showcaseitem.productid || "No Product ID"}</p>
                                </div>
                            ))}
                        </div>
                    </main>
                </div>
                <div className="flex justify-center mt-8">
                    <CTAExploreMore
                        text={subCategory?.replace(/-/g, " ").replace(/\b\w/g, (l: string) => l.toUpperCase()) || "Sub-Categories"}
                        suffix="Products"
                        to={`/products/${category}/${subCategory}`}
                    />
                    
                </div>
            </div>
        )
    );
}